/**
 * @fileoverview Main Click&Pledge component for web platform.
 * Handles the complete donation flow including form loading, user authentication,
 * and navigation between different donation pages.
 */

import CryptoJS from 'crypto-js';
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { translate } from '../../../base/i18n';
import { Icon, IconClickandPledge, IconClose } from '../../../base/icons';
import { getLocalParticipant, isLocalParticipantModerator } from '../../../base/participants';
import { bindMeetData, isClickandPledge, setPopupOverlay } from '../../actions';

import AutoRegisterPage from './pages/AutoRegisterPage';
import Loading from './pages/Loading';
import PublishForm from './pages/PublishForm';
import Success from './pages/Success';
import UserDonationHome from './pages/UserDonationHome';
import { CNPPopupOverlayStyles } from './styles';
import { PAGES } from './utility/pages';

// const UserPage = lazy(() => import('./pages/UserDonationHome'));

/**
 * Main Click&Pledge component that manages the donation flow.
 * Handles user authentication, form loading, and navigation between different pages
 * based on user role (host/guest) and authentication status.
 *
 * @param {Object} props - Component props
 * @param {Object} props.meetHourFinalData - Meeting and user data from MeetHour
 * @returns {ReactElement} The rendered Click&Pledge interface
 */
function ClickAndPledge({ meetHourFinalData, t }) {
    const [ userData, setUserData ] = useState(null);
    const [ meetingData, setMeetingData ] = useState(null);
    const [ currentPage, setCurrentPage ] = useState(PAGES.LOADING);
    const [ pageState, setPageState ] = useState({});
    const [ missingInfoDialog, setMissingInfoDialog ] = useState(null);
    const brandLogo = useSelector(state => state['features/clickandpledge']?.formSettings?.LogoURL);
    const isPersonModerator = useSelector(isLocalParticipantModerator);
    const formGUID = useSelector(state => state['features/clickandpledge']?.formGUID);
    const renderPopupOverlay = useSelector(state => state['features/clickandpledge']?.renderPopupOverlay);
    const userId = useSelector(state => state['features/clickandpledge']?.userId);
    const meetData = useSelector(state => state['features/clickandpledge']?.meetData || {});
    const meetFormData = useSelector(state => state['features/clickandpledge']?.meetFormData || []);
    const localParticipant = useSelector(getLocalParticipant);
    const localParticipantId = localParticipant?.id;
    const _isClickandPledgeEnabled = useSelector(state => state['features/clickandpledge'].isEnabled);
    const processingTimeoutRef = useRef(null);
    const serverName = '';
    const versionNumber = '';
    const style = { marginRight: '10px' };

    // Use ref to track if form check has been initiated to prevent duplicate API calls
    const formCheckInitiated = useRef(false);

    const dispatch = useDispatch();

    /**
     * Navigation function to switch between different pages in the donation flow.
     *
     * @param {string} page - The page to navigate to (from PAGES constants)
     * @param {Object} state - Optional state to pass to the new page
     */
    const navigate = useCallback((page, state = {}) => {
        setPageState(state);
        setCurrentPage(page);
    }, []);

    // Initialize data from props
    useEffect(() => {
        if (meetHourFinalData) {
            const meeting = {
                meetingUUID: meetHourFinalData.MeetingID
            };

            const nameParts = (meetHourFinalData.HostContactName || '').trim().split(/\s+/);
            const user = {
                email: meetHourFinalData.HostEmail,
                display_name: meetHourFinalData.HostContactName,
                first_name: nameParts[0] || '',
                last_name: nameParts.slice(1).join(' ') || '',
                userId: meetHourFinalData.HostUserID,
                accountId: meetHourFinalData.HostAcctID,
                hostId: meetHourFinalData.HostID,
                meetingId: meetHourFinalData.MeetingID,
                startTime: meetHourFinalData.MeetingStartTime,
                givenToken: meetHourFinalData.GiventToken
            };

            setMeetingData(meeting);
            setUserData(user);
            dispatch(bindMeetData(user));
        }
    }, [ meetHourFinalData, dispatch ]);

    /**
     * Check if the donation amount is valid and prompt for missing contact info.
     * Always shows all three fields; existing values are pre-filled and disabled.
     * Missing values are empty and editable. On submit, persists to Redux meetData.
     *
     * @param {Function} onConfirm - called with { fname, lname, email } after the user fills gaps.
     * @returns {boolean} true if all values present (onConfirm called immediately), false if dialog shown.
     */
    const requireContactInfo = useCallback(onConfirm => {
        const paymentData = meetFormData?.formdata?.[0]?.payment || {};
        const fname = meetData?.first_name || paymentData.first_name || '';
        const lname = meetData?.last_name || paymentData.last_name || '';
        const email = meetData?.email || paymentData.email || '';

        if (fname && lname && email) {
            onConfirm({
                fname,
                lname,
                email
            });

            return true;
        }

        // Show dialog — pre-fill existing values, leave missing ones empty
        setMissingInfoDialog({
            fname,
            lname,
            email,
            fnameDisabled: Boolean(fname),
            lnameDisabled: Boolean(lname),
            emailDisabled: Boolean(email),
            onConfirm
        });

        return false;
    }, [ meetData, meetFormData ]);

    const focusPopupWindow = () => {
        if (renderPopupOverlay?.popupWindow) {
            renderPopupOverlay.popupWindow.focus();
        }
    };

    const closePane = () => {
        dispatch(isClickandPledge(!_isClickandPledgeEnabled));
    };

    const closePopupWindow = () => {
        if (renderPopupOverlay?.popupWindow && !renderPopupOverlay.popupWindow.closed) {
            renderPopupOverlay.popupWindow.close();
        }

        dispatch(setPopupOverlay(false, null));
    };

    // Auto-dismiss the processing overlay after 15s to prevent it from getting stuck
    useEffect(() => {
        const isSpinnerOnly = renderPopupOverlay?.isVisible
            && (!renderPopupOverlay?.popupWindow || renderPopupOverlay?.popupWindow?.closed);

        if (isSpinnerOnly) {
            // Start 15-second timeout
            processingTimeoutRef.current = window.setTimeout(() => {
                closePopupWindow();
            }, 15000);
        } else if (processingTimeoutRef.current) {
            window.clearTimeout(processingTimeoutRef.current);
            processingTimeoutRef.current = null;
        }

        return () => {
            if (processingTimeoutRef.current) {
                window.clearTimeout(processingTimeoutRef.current);
                processingTimeoutRef.current = null;
            }
        };
    }, [ renderPopupOverlay?.isVisible, renderPopupOverlay?.popupWindow ]);

    // Check if form exists for this meeting - runs only once when userData and meetingData are available
    useEffect(() => {
        // Guard: only run if we have data and haven't already initiated the check
        if (!userData || !meetingData || formCheckInitiated.current) {
            return;
        }

        // Mark as initiated to prevent duplicate calls
        formCheckInitiated.current = true;

        // Check if user is not moderator and not owner (non-host/non-moderator)
        if (formGUID && userId !== localParticipantId) {
            // Non-moderator/non-owner with formGUID - skip auto-registration and authentication
            // Go directly to form page without auto authentication
            navigate(PAGES.LOAD_FORM, {
                showDForm: true,
                skipAuthentication: true,
                directFormLoad: true
            });

            return;
        }

        // For hosts/moderators or when formGUID not available, proceed with auto-registration
        const userparams = { eId: meetingData.meetingUUID };

        // Encrypt user params for API call (currently commented out)
        CryptoJS.AES.encrypt(
            JSON.stringify({ body: { userparams } }),
            'gatheringjar$@0047'
        ).toString();

        setCurrentPage(PAGES.AUTO_REGISTER);

    }, [ userData, meetingData, navigate, isPersonModerator, formGUID ]);

    /**
     * Renders the appropriate page component based on current state.
     * Handles loading states and switches between host, user, and success pages.
     *
     * @returns {ReactElement} The current page component
     */
    const renderCurrentPage = () => {
        if (!userData || !meetingData) {
            return <Loading />;
        }

        switch (currentPage) {
        case PAGES.AUTO_REGISTER:
            return (
                <AutoRegisterPage
                    meetingData = { meetingData }
                    onNavigate = { navigate }
                    serverName = { serverName }
                    userData = { userData }
                    versionNumber = { versionNumber } />
            );
        case PAGES.PUBLISH_FORM:
            return (
                <PublishForm
                    meetingData = { meetingData }
                    onNavigate = { navigate }
                    userData = { userData } />
            );

        case PAGES.LOAD_FORM:
            return (
                <UserDonationHome
                    directFormLoad = { pageState.directFormLoad ?? false }
                    hideHeader = { pageState.hideHeader ?? false }
                    ishost = { isPersonModerator }
                    meetingData = { meetingData }
                    onNavigate = { navigate }
                    requireContactInfo = { requireContactInfo }
                    serverName = { serverName }
                    showDForm = { pageState.showDForm ?? true }
                    skipAuthentication = { pageState.skipAuthentication ?? false }
                    userData = { userData }
                    versionNumber = { versionNumber } />
            );

        case PAGES.SUCCESS:
            return (
                <Success
                    donateAgain = { true }
                    onNavigate = { navigate }
                    totalAmount = { pageState.totalAmount }
                    udata = { pageState.udata } />
            );

        case PAGES.STREAMER:
            return (
                <UserDonationHome
                    directFormLoad = { pageState.directFormLoad ?? false }
                    hideHeader = { pageState.hideHeader ?? false }
                    ishost = { isPersonModerator }
                    meetingData = { meetingData }
                    onNavigate = { navigate }
                    requireContactInfo = { requireContactInfo }
                    serverName = { serverName }
                    showDForm = { false }
                    skipAuthentication = { pageState.skipAuthentication ?? false }
                    userData = { userData }
                    versionNumber = { versionNumber } />
            );

        case PAGES.LOADING:
        default:
            return <Loading />;
        }
    };


    return (
        <div
            className = 'participants_pane'
            style = { style }>
            <div className = 'clickandpledge-header'>
                <div className = 'default'><Icon src = { IconClickandPledge } /> <span>{t('clickandpledge.GiventBrand')}</span></div>
                <div className = ' clickandpledge-close-parent'>
                    <div className = 'logo'>
                        <img
                            alt = { t('clickandpledge.LogoAlt') }
                            className = 'streamer-logo-img'
                            onError = { e => {
                                e.target.onerror = null;
                                e.target.src = `/images/clickandpledge/givent_logo_1.png?${new Date().getTime()}`;
                            } }
                            src = { brandLogo || '/images/clickandpledge/givent_logo_1.png' } />
                    </div>
                    <Icon
                        onClick = { closePane }
                        src = { IconClose } />
                </div>
            </div>
            <Suspense fallback = { <Loading /> }>
                {renderCurrentPage()}
            </Suspense>
            {renderPopupOverlay.isVisible && (
                <div className = 'popup-overlay'>
                    {!renderPopupOverlay?.popupWindow || renderPopupOverlay?.popupWindow?.closed ? (
                        <>
                            <div style = { CNPPopupOverlayStyles.loaderContainer }>
                                <div className = 'cnp-overlay-spinner' />
                            </div>
                            <span style = { CNPPopupOverlayStyles.overlayText }>
                                {t('clickandpledge.processing') || 'Processing...'}
                            </span>
                        </>
                    ) : (
                        <>
                            <span style = { CNPPopupOverlayStyles.overlayText }>
                                {t('clickandpledge.CompletePaymentMessage')}
                            </span>
                            <button
                                className = 'focus-popupwindow'
                                onClick = { focusPopupWindow }>
                                {t('clickandpledge.ContinuePayment')}
                            </button>
                            <button
                                className = 'close-popupwindow'
                                onClick = { closePopupWindow }>
                                {t('clickandpledge.CancelPayment')}
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* ─── Missing-info dialog (scoped to pane) ─── */}
            {missingInfoDialog && (
                <div className = 'cnp-missing-info-overlay'>
                    <div className = 'cnp-missing-info-dialog'>
                        <h4
                            className = 'cnp-missing-info-title'>{t('clickandpledge.ContactInfoRequired') || 'Contact info required'}</h4>
                        <p
                            className = 'cnp-missing-info-desc'>
                            {t('clickandpledge.PleaseFillMissingFields') || 'Please fill in the missing fields to proceed.'}
                        </p>
                        <input
                            className = 'cnp-missing-info-input'
                            onChange = { e => setMissingInfoDialog(prev => {
                                return {
                                    ...prev,
                                    fname: e.target.value
                                };
                            }) }
                            placeholder = { t('clickandpledge.firstName') || 'First Name' }
                            type = 'text'
                            value = { missingInfoDialog.fname } />
                        <input
                            className = 'cnp-missing-info-input'
                            onChange = { e => setMissingInfoDialog(prev => {
                                return {
                                    ...prev,
                                    lname: e.target.value
                                };
                            }) }
                            placeholder = { t('clickandpledge.lastName') || 'Last Name' }
                            type = 'text'
                            value = { missingInfoDialog.lname } />
                        <input
                            className = 'cnp-missing-info-input'
                            disabled = { missingInfoDialog.emailDisabled }
                            onChange = { e => setMissingInfoDialog(prev => {
                                return {
                                    ...prev,
                                    email: e.target.value
                                };
                            }) }
                            placeholder = { t('clickandpledge.email') || 'Email' }
                            type = 'email'
                            value = { missingInfoDialog.email } />
                        <div
                            className = 'cnp-missing-info-actions'>
                            <button
                                className = 'cnp-missing-info-btn cnp-missing-info-cancel'
                                onClick = { () => setMissingInfoDialog(null) }
                                type = 'button'>
                                {t('clickandpledge.Cancel') || 'Cancel'}
                            </button>
                            <button
                                className = 'cnp-missing-info-btn cnp-missing-info-submit'
                                onClick = { () => {
                                    const { fname, lname, email, onConfirm } = missingInfoDialog;

                                    if (!fname || !lname || !email) {
                                        return;
                                    }

                                    // Persist to Redux so the dialog won't appear again
                                    dispatch(bindMeetData({
                                        ...meetData,
                                        first_name: fname,
                                        last_name: lname,
                                        display_name: `${fname} ${lname}`.trim(),
                                        email
                                    }));

                                    setMissingInfoDialog(null);
                                    onConfirm({
                                        fname,
                                        lname,
                                        email
                                    });
                                } }
                                type = 'button'>
                                {t('clickandpledge.Continue') || 'Continue'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default translate(ClickAndPledge);
